import { useEffect, useMemo, useState, ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import Button from 'components/Shared/Button'
import Icon from 'components/Shared/Icon'
import Page from 'components/Shared/Page'
import Tooltip from 'components/Shared/Tooltip'

import { adaloBackendAxios } from 'utils/io/http/axios'

import './Dashboard.scss'
import './PQLFunnel.scss'

type UsersTimeseriesRow = {
  date: string
  total_count: number
  google_signup_count: number
  confirmed_count: number
  trial_count: number
  clicked_preview: number
  added_screen: number
  added_component: number
  created_collection: number
  clicked_add_integration: number
  added_integration: number
  published_app: number
  unpublished_app: number
  clicked_share: number
  clicked_sheetbridge: number
  sheetbridge_got_bridges: number
  sheetbridge_added_collections: number
  viewed_billing_tab: number
  clicked_upgrade_starter: number
  clicked_upgrade_pro: number
  clicked_upgrade_team: number
  clicked_upgrade_business: number
}

const PAGE_SIZE = 30

const PQLFunnel = (): ReactElement => {
  const { t } = useTranslation()
  const [usersSeries, setUsersSeries] = useState<UsersTimeseriesRow[]>([])
  const [pagination, setPagination] = useState<{
    total: number
    offset: number
    limit: number
  }>({
    total: 0,
    offset: 0,
    limit: PAGE_SIZE,
  })
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [csvLoading, setCsvLoading] = useState<boolean>(false)

  const totalPages = Math.ceil(pagination.total / PAGE_SIZE)
  const currentPage = Math.floor(pagination.offset / PAGE_SIZE) + 1

  const fetchData = async (offset: number, limit: number) => {
    setLoading(true)
    setError(null)
    try {
      // Backend expects offset/limit
      const url = `/admin/dashboard/pql?offset=${encodeURIComponent(
        offset
      )}&limit=${encodeURIComponent(limit)}`
      const { data: results } = await adaloBackendAxios.get<unknown>(url)

      const { data, pagination: paginationData } = (results || {}) as {
        data: UsersTimeseriesRow[]
        pagination: {
          total: number
          offset: number
          limit: number
        }
      }
      setUsersSeries(Array.isArray(data) ? data : [])
      setPagination(paginationData)
    } catch (err) {
      console.error('Failed to fetch PQL funnel data', err)
      setError('Failed to load funnel metrics.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchData(0, PAGE_SIZE)
  }, [])

  const sortedUsersSeries = useMemo(() => {
    const series = usersSeries || []
    const byDate = [...series]
    byDate.sort((a, b) => {
      if (sortOrder === 'desc') {
        if (a.date > b.date) return -1
        if (a.date < b.date) return 1

        return 0
      }
      if (a.date < b.date) return -1
      if (a.date > b.date) return 1

      return 0
    })

    return byDate
  }, [usersSeries, sortOrder])

  const handleSortToggle = () => {
    setSortOrder(prevOrder => (prevOrder === 'desc' ? 'asc' : 'desc'))
  }

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      const offset = (page - 1) * PAGE_SIZE
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      fetchData(offset, PAGE_SIZE)
    }
  }

  const handleCsvDownload = async () => {
    setCsvLoading(true)
    setError(null)
    try {
      const { data } = await adaloBackendAxios.post<Blob>(
        '/admin/dashboard/pql/csv-export',
        null,
        { responseType: 'blob' }
      )

      const url = URL.createObjectURL(data)
      const a = document.createElement('a')
      a.href = url
      a.download = `pql-users-${new Date().toISOString().slice(0, 10)}.csv`
      a.style.display = 'none'
      document.body.appendChild(a)
      a.click()

      requestAnimationFrame(() => {
        URL.revokeObjectURL(url)
        document.body.removeChild(a)
      })
    } catch (err) {
      console.error('CSV Export Failed:', err)
      setError('Failed to download CSV.')
    } finally {
      setCsvLoading(false)
    }
  }

  return (
    <Page className="admin-dashboard-page" title="PQL Funnel">
      {error ? <div className="pql-funnel-error">{error}</div> : null}
      <div className="pql-funnel-actions">
        <Button small onClick={handleCsvDownload} loading={csvLoading}>
          Download User CSV
        </Button>
      </div>
      <div className={classNames('pql-funnel-container', { loading })}>
        <div className="pql-funnel-table-wrapper">
          <table className="pql-funnel-table">
            <thead>
              <tr>
                <th className="pql-funnel-header pql-funnel-header--date">
                  <Tooltip tooltip="Signup date" placement="bottom" small>
                    <button
                      type="button"
                      className="pql-funnel-sort-button"
                      onClick={handleSortToggle}
                    >
                      Date
                      <Icon
                        type={
                          sortOrder === 'desc'
                            ? 'down-arrow-small'
                            : 'up-arrow-small'
                        }
                        small
                      />
                    </button>
                  </Tooltip>
                </th>
                <th className="pql-funnel-header pql-funnel-header--numeric">
                  <Tooltip tooltip="Total signups" placement="bottom" small>
                    Signups
                  </Tooltip>
                </th>
                <th className="pql-funnel-header pql-funnel-header--numeric">
                  <Tooltip
                    tooltip="Signups with Google linked at registration"
                    placement="bottom"
                    small
                  >
                    Google signups
                  </Tooltip>
                </th>
                <th className="pql-funnel-header pql-funnel-header--numeric">
                  <Tooltip
                    tooltip="Confirmed / verified email"
                    placement="bottom"
                    small
                  >
                    Verified
                  </Tooltip>
                </th>
                <th className="pql-funnel-header pql-funnel-header--numeric">
                  <Tooltip tooltip="Started a trial" placement="bottom" small>
                    Trial
                  </Tooltip>
                </th>
                <th className="pql-funnel-header pql-funnel-header--numeric">
                  <Tooltip
                    tooltip="Clicked app preview"
                    placement="bottom"
                    small
                  >
                    Preview
                  </Tooltip>
                </th>
                <th className="pql-funnel-header pql-funnel-header--numeric">
                  <Tooltip tooltip="Added a screen" placement="bottom" small>
                    Screens
                  </Tooltip>
                </th>
                <th className="pql-funnel-header pql-funnel-header--numeric">
                  <Tooltip tooltip="Added a component" placement="bottom" small>
                    Components
                  </Tooltip>
                </th>
                <th className="pql-funnel-header pql-funnel-header--numeric">
                  <Tooltip
                    tooltip="Created a collection"
                    placement="bottom"
                    small
                  >
                    Collections
                  </Tooltip>
                </th>
                <th className="pql-funnel-header pql-funnel-header--numeric">
                  <Tooltip
                    tooltip="Clicked add integration"
                    placement="bottom"
                    small
                  >
                    Clicked Int.
                  </Tooltip>
                </th>
                <th className="pql-funnel-header pql-funnel-header--numeric">
                  <Tooltip
                    tooltip="Added an integration"
                    placement="bottom"
                    small
                  >
                    Integrations
                  </Tooltip>
                </th>
                <th className="pql-funnel-header pql-funnel-header--numeric">
                  <Tooltip tooltip="Published an app" placement="bottom" small>
                    Published
                  </Tooltip>
                </th>
                <th className="pql-funnel-header pql-funnel-header--numeric">
                  <Tooltip
                    tooltip="Clicked share button"
                    placement="bottom"
                    small
                  >
                    Share Clicks
                  </Tooltip>
                </th>
                <th className="pql-funnel-header pql-funnel-header--numeric">
                  <Tooltip
                    tooltip="Clicked SheetBridge"
                    placement="bottom"
                    small
                  >
                    SheetBridge
                  </Tooltip>
                </th>
                <th className="pql-funnel-header pql-funnel-header--numeric">
                  <Tooltip
                    tooltip="SheetBridge — got bridges"
                    placement="bottom"
                    small
                  >
                    SB Bridges
                  </Tooltip>
                </th>
                <th className="pql-funnel-header pql-funnel-header--numeric">
                  <Tooltip
                    tooltip="SheetBridge — added collections"
                    placement="bottom"
                    small
                  >
                    SB Collections
                  </Tooltip>
                </th>
                <th className="pql-funnel-header pql-funnel-header--numeric">
                  <Tooltip
                    tooltip="Viewed the billing tab"
                    placement="bottom"
                    small
                  >
                    Billing Views
                  </Tooltip>
                </th>
                <th className="pql-funnel-header pql-funnel-header--numeric">
                  <Tooltip
                    tooltip="Clicked upgrade to Starter"
                    placement="bottom"
                    small
                  >
                    Clicked Starter
                  </Tooltip>
                </th>
                <th className="pql-funnel-header pql-funnel-header--numeric">
                  <Tooltip
                    tooltip="Clicked upgrade to Pro"
                    placement="bottom"
                    small
                  >
                    Clicked Pro
                  </Tooltip>
                </th>
                <th className="pql-funnel-header pql-funnel-header--numeric">
                  <Tooltip
                    tooltip="Clicked upgrade to Team"
                    placement="bottom"
                    small
                  >
                    Clicked Team
                  </Tooltip>
                </th>
                <th className="pql-funnel-header pql-funnel-header--numeric">
                  <Tooltip
                    tooltip="Clicked upgrade to Business"
                    placement="bottom"
                    small
                  >
                    Clicked Business
                  </Tooltip>
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedUsersSeries.map(row => (
                <tr key={row.date}>
                  <td className="pql-funnel-cell pql-funnel-cell--date">
                    {row.date}
                  </td>
                  <td className="pql-funnel-cell pql-funnel-cell--numeric">
                    {row.total_count}
                  </td>
                  <td className="pql-funnel-cell pql-funnel-cell--numeric">
                    {row.google_signup_count ?? 0}
                  </td>
                  <td className="pql-funnel-cell pql-funnel-cell--numeric">
                    {row.confirmed_count}
                  </td>
                  <td className="pql-funnel-cell pql-funnel-cell--numeric">
                    {row.trial_count}
                  </td>
                  <td className="pql-funnel-cell pql-funnel-cell--numeric">
                    {row.clicked_preview}
                  </td>
                  <td className="pql-funnel-cell pql-funnel-cell--numeric">
                    {row.added_screen}
                  </td>
                  <td className="pql-funnel-cell pql-funnel-cell--numeric">
                    {row.added_component}
                  </td>
                  <td className="pql-funnel-cell pql-funnel-cell--numeric">
                    {row.created_collection}
                  </td>
                  <td className="pql-funnel-cell pql-funnel-cell--numeric">
                    {row.clicked_add_integration}
                  </td>
                  <td className="pql-funnel-cell pql-funnel-cell--numeric">
                    {row.added_integration}
                  </td>
                  <td className="pql-funnel-cell pql-funnel-cell--numeric">
                    {row.published_app}
                  </td>
                  <td className="pql-funnel-cell pql-funnel-cell--numeric">
                    {row.clicked_share}
                  </td>
                  <td className="pql-funnel-cell pql-funnel-cell--numeric">
                    {row.clicked_sheetbridge}
                  </td>
                  <td className="pql-funnel-cell pql-funnel-cell--numeric">
                    {row.sheetbridge_got_bridges}
                  </td>
                  <td className="pql-funnel-cell pql-funnel-cell--numeric">
                    {row.sheetbridge_added_collections}
                  </td>
                  <td className="pql-funnel-cell pql-funnel-cell--numeric">
                    {row.viewed_billing_tab}
                  </td>
                  <td className="pql-funnel-cell pql-funnel-cell--numeric">
                    {row.clicked_upgrade_starter}
                  </td>
                  <td className="pql-funnel-cell pql-funnel-cell--numeric">
                    {row.clicked_upgrade_pro}
                  </td>
                  <td className="pql-funnel-cell pql-funnel-cell--numeric">
                    {row.clicked_upgrade_team}
                  </td>
                  <td className="pql-funnel-cell pql-funnel-cell--numeric">
                    {row.clicked_upgrade_business}
                  </td>
                </tr>
              ))}
              {sortedUsersSeries.length === 0 && !loading ? (
                <tr>
                  <td
                    colSpan={21}
                    className="pql-funnel-cell pql-funnel-cell--empty"
                  >
                    No data
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        {pagination.total > 0 && (
          <div className="pql-funnel-pagination">
            <div className="pagination-info">
              {t('Page')} {currentPage} {t('of')} {totalPages} (
              {pagination.total} records)
            </div>
            {totalPages > 1 && (
              <div className="pagination-controls">
                <Button
                  small
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(1)}
                >
                  {t('First Page')}
                </Button>
                <Button
                  small
                  text
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  <Icon type="arrow-back" small />
                </Button>
                <span className="page-numbers">
                  {currentPage} / {totalPages}
                </span>
                <Button
                  small
                  text
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  <Icon type="arrow-next" small />
                </Button>
                <Button
                  small
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(totalPages)}
                >
                  {t('Last Page')}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </Page>
  )
}

export default PQLFunnel
